import { msg } from "@lingui/core/macro";
import { type Component, Show } from "solid-js";
import { AlertCircle, RefreshCw } from "@fleet/icons";
import { cn } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "./button";

interface QueryErrorStateProps {
  /**
   * `data-testid` of the error box. Convention: `<screen>-error`.
   * It's the anchor of the I3 spec: the probe requires THIS testid to be visible and
   * the EMPTY state's testid to have `toHaveCount(0)`.
   */
  testId: string;
  /** Custom title. Default: "Couldn't load this". */
  title?: string;
  /** Custom description. Default: explains that it's a connection failure. */
  description?: string;
  /**
   * Retry. Must retry FOR REAL (`query.refetch()`), not reload the
   * page nor clear the state: a button that doesn't re-request is as dishonest
   * as having no error state.
   */
  onRetry?: () => void;
  /** Compact variant for sections inside a `<Card>`. */
  compact?: boolean;
  class?: string;
}

/**
 * ERROR state of a query — the third state that almost never gets written.
 *
 * P23 / CC-60 · invariant I3: "no response" ≠ "nothing". A screen
 * with only loading + empty paints a network failure as "you have no items", and
 * lies to the user about the state of their own product. On the profile that
 * even said "No active sessions found" when the API hadn't responded:
 * a lie with security consequences (the user believes nobody else
 * has an open session).
 *
 * Deliberately does NOT reuse `EmptyState`: it must look as LITTLE as possible
 * like empty. Alert icon in a destructive tone, its own border and an explicit
 * retry.
 */
export const QueryErrorState: Component<QueryErrorStateProps> = (props) => {
  const { t } = useI18n();

  return (
    <div
      data-testid={props.testId}
      role="alert"
      class={cn(
        "flex flex-col items-center gap-3 rounded-xl border border-destructive/25 bg-destructive/5 text-center",
        props.compact ? "p-4" : "p-8",
        props.class,
      )}
    >
      <div class="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertCircle class="h-5 w-5" />
      </div>
      <div class="space-y-1">
        <p class="text-sm font-semibold text-foreground">
          {props.title ?? t(msg`Couldn't load this`)}
        </p>
        <p class="max-w-xs text-xs text-muted-foreground text-balance">
          {props.description ??
            t(msg`We couldn't reach the server. This is a connection problem, not empty data.`)}
        </p>
      </div>
      <Show when={props.onRetry}>
        {(retry) => (
          <Button
            variant="outline"
            size="sm"
            class="gap-2"
            onClick={() => {
              retry()();
            }}
          >
            <RefreshCw class="h-3.5 w-3.5" />
            {t(msg`Retry`)}
          </Button>
        )}
      </Show>
    </div>
  );
};
